import React, { useState, useEffect, useCallback } from 'react';
import { authHeaders } from '../authToken';

const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * Theme A flashcards for one or more selected learning statements, or — in
 * review mode — the cards due for spaced-repetition across all of Theme A.
 *
 * Props:
 *   scopes      array of syllabus prefixes, e.g. ['A1.3.1', 'A1.3.2']
 *   level       'sl' | 'hl' (changes which HL-only items appear)
 *   scopeTitle  human-readable heading
 *   reviewMode  when true, ignore scopes and load due cards (/due-cards)
 *   onBack      return to the accordion
 */
export default function Flashcards({ scopes = [], level, scopeTitle, reviewMode = false, onBack }) {
  const [terms,   setTerms]   = useState([]);
  const [idx,     setIdx]     = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [loading, setLoading] = useState(true);

  // Stable string key so the effect only re-fires when the scope list actually changes
  const scopeKey = scopes.slice().sort().join(',');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        let merged;
        if (reviewMode) {
          const r = await fetch(`${API_URL}/api/theme-a/due-cards?level=${level}`, { headers: authHeaders() });
          const d = await r.json();
          merged = d.terms || [];
        } else {
          const results = await Promise.all(
            scopeKey.split(',').filter(Boolean).map(s =>
              fetch(`${API_URL}/api/theme-a/content/${s}?level=${level}`, { headers: authHeaders() })
                .then(r => r.json())
            )
          );
          const seen = new Set();
          merged = [];
          for (const d of results) {
            for (const t of (d.terms || [])) {
              if (!seen.has(t.id)) { seen.add(t.id); merged.push(t); }
            }
          }
        }
        if (!cancelled) {
          setTerms(merged);
          setIdx(0);
          setFlipped(false);
        }
      } catch (_) {
        if (!cancelled) setTerms([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [scopeKey, level, reviewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const current = terms[idx];

  const go = useCallback((delta) => {
    setFlipped(false);
    setIdx(i => Math.min(Math.max(i + delta, 0), terms.length - 1));
  }, [terms.length]);

  const review = useCallback(async (confidence) => {
    if (!current) return;
    // Fire-and-forget — the review is personal-prioritisation only.
    try {
      await fetch(`${API_URL}/api/theme-a/flashcard-review`, {
        method:  'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body:    JSON.stringify({ term_id: current.id, confidence }),
      });
    } catch (_) {}
    if (idx < terms.length - 1) go(1);
  }, [current, idx, terms.length, go]);

  return (
    <div className="ta-canvas">
      <div className="ta-header">
        <button className="ta-back" onClick={onBack}>‹ Topics</button>
        <div>
          <h1 className="ta-title">{reviewMode ? 'Review due' : 'Flashcards'}</h1>
          <p className="ta-subtitle">{scopeTitle} · {level.toUpperCase()}</p>
        </div>
      </div>

      {loading ? (
        <div className="ta-empty">Loading…</div>
      ) : terms.length === 0 ? (
        <div className="ta-empty">
          {reviewMode ? '🎉 You’re all caught up — no cards due for review.'
                      : 'No flashcards for this selection.'}
        </div>
      ) : (
        <div className="ta-study">
          <div className="ta-progress">{idx + 1} / {terms.length}</div>

          <div
            className={`ta-flip${flipped ? ' ta-flipped' : ''}`}
            onClick={() => setFlipped(f => !f)}
            role="button"
            tabIndex={0}
            aria-label="Flip flashcard"
            onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); setFlipped(f => !f); } }}
          >
            <div className="ta-flip-inner">
              <div className="ta-face ta-face-front">
                <div className="ta-face-label">Term</div>
                <div className="ta-term">{current.term}</div>
                <div className="ta-flip-hint">Click to reveal definition</div>
              </div>
              <div className="ta-face ta-face-back">
                <div className="ta-face-label">Definition</div>
                <div className="ta-definition">{current.definition}</div>
                {current.diagram && (
                  <div
                    className="ta-diagram"
                    /* Diagrams are trusted inline SVG authored in the curriculum files. */
                    dangerouslySetInnerHTML={{ __html: current.diagram }}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="ta-controls">
            <button className="ta-nav-btn" onClick={() => go(-1)} disabled={idx === 0}>‹ Prev</button>
            <button className="ta-thumb ta-thumb-down" onClick={() => review('down')}>👎 Review again</button>
            <button className="ta-thumb ta-thumb-up" onClick={() => review('up')}>👍 Got it</button>
            <button className="ta-nav-btn" onClick={() => go(1)} disabled={idx === terms.length - 1}>Next ›</button>
          </div>
        </div>
      )}
    </div>
  );
}
